import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  SapSyncJob,
  SapDlqEntry,
  SapDashboardStatus,
  SapSyncFilter,
  PaginationMeta,
  SyncJobsResponse,
} from '../domain/types';
import { DEFAULT_PAGE_SIZE } from '../domain/constants';

export interface SapSyncState {
  dashboard: SapDashboardStatus | null;
  syncJobs: SapSyncJob[];
  pagination: PaginationMeta;
  dlqEntries: SapDlqEntry[];
  filter: SapSyncFilter;
  loading: boolean;
  error: string | null;
  selectedJob: SapSyncJob | null;
}

const initialState: SapSyncState = {
  dashboard: null,
  syncJobs: [],
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE, pageCount: 1, total: 0 },
  dlqEntries: [],
  filter: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
  loading: false,
  error: null,
  selectedJob: null,
};

const sapSyncSlice = createSlice({
  name: 'sapSync',
  initialState,
  reducers: {
    // Dashboard
    fetchDashboardRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDashboardSuccess: (state, action: PayloadAction<SapDashboardStatus>) => {
      state.dashboard = action.payload;
      state.loading = false;
    },
    fetchDashboardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Sync jobs
    fetchSyncJobsRequest: (state, _action: PayloadAction<SapSyncFilter | undefined>) => {
      state.loading = true;
      state.error = null;
    },
    fetchSyncJobsSuccess: (state, action: PayloadAction<SyncJobsResponse>) => {
      state.syncJobs = action.payload.data;
      state.pagination = action.payload.meta;
      state.loading = false;
    },
    fetchSyncJobsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // DLQ
    fetchDlqRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDlqSuccess: (state, action: PayloadAction<SapDlqEntry[]>) => {
      state.dlqEntries = action.payload;
      state.loading = false;
    },
    fetchDlqFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // DLQ retry
    retryDlqRequest: (state, _action: PayloadAction<number>) => {
      state.loading = true;
    },
    retryDlqSuccess: (state, action: PayloadAction<number>) => {
      state.dlqEntries = state.dlqEntries.filter((e) => e.id !== action.payload);
      state.loading = false;
      if (state.dashboard) {
        state.dashboard.dlqDepth = Math.max(0, state.dashboard.dlqDepth - 1);
      }
    },
    retryDlqFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Filter
    setFilter: (state, action: PayloadAction<Partial<SapSyncFilter>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    setSort: (state, action: PayloadAction<{ sortField: string; sortOrder: 'asc' | 'desc' }>) => {
      state.filter.sortField = action.payload.sortField;
      state.filter.sortOrder = action.payload.sortOrder;
      state.filter.page = 1;
    },

    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload;
      state.filter.page = 1;
    },

    // Selection
    selectJob: (state, action: PayloadAction<SapSyncJob | null>) => {
      state.selectedJob = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const sapSyncActions = sapSyncSlice.actions;
export const sapSyncReducer = sapSyncSlice.reducer;
